import type {
  AuditEntry,
  FleetCheck,
  PlatformIncident,
  RevenueMonth,
  StaffMember,
  WorkerRegion,
} from '../types/admin'
import { daysFromNow, minutesFromNow } from '../utils/datetime'

export const workerRegions: WorkerRegion[] = [
  {
    id: 'w-fra',
    name: 'Frankfurt',
    code: 'eu-central',
    state: 'ok',
    queueDepth: 12,
    checksPerMinute: 1840,
    p95Ms: 268,
    lastHeartbeatAt: minutesFromNow(-1),
  },
  {
    id: 'w-iad',
    name: 'Virginia',
    code: 'us-east',
    state: 'degraded',
    queueDepth: 486,
    checksPerMinute: 1120,
    p95Ms: 1490,
    lastHeartbeatAt: minutesFromNow(-1),
  },
  {
    id: 'w-sin',
    name: 'Singapore',
    code: 'ap-southeast',
    state: 'ok',
    queueDepth: 8,
    checksPerMinute: 940,
    p95Ms: 312,
    lastHeartbeatAt: minutesFromNow(-2),
  },
]

export const platformIncidents: PlatformIncident[] = [
  {
    id: 'i-77',
    title: 'Virginia workers behind on the ping queue',
    body: 'Queue depth on us-east has been above 400 for 26 minutes. Uptime checks for customers pinned to that region are running 3–4 minutes late. Certificate sweeps are unaffected.',
    severity: 'warning',
    startedAt: minutesFromNow(-26),
    resolvedAt: null,
    affected: '318 monitors · 41 accounts',
  },
  {
    id: 'i-76',
    title: 'Alert email delivery delayed via primary provider',
    body: 'Outbound alerts queued for 9 minutes before failing over to the secondary sender. All queued mail was delivered; nothing was dropped.',
    severity: 'critical',
    startedAt: daysFromNow(-2, 14),
    resolvedAt: daysFromNow(-2, 15),
    affected: '1,204 emails',
  },
  {
    id: 'i-75',
    title: 'Certificate sweep ran 40 minutes late',
    body: 'A slow migration held the 08:00 sweep. Day counts were correct once it completed and no thresholds were missed.',
    severity: 'warning',
    startedAt: daysFromNow(-11, 8),
    resolvedAt: daysFromNow(-11, 9),
    affected: 'All accounts',
  },
]

export const fleetChecks: FleetCheck[] = [
  {
    id: 'f-1',
    domain: 'api.acme.com',
    customer: 'Acme Internet',
    customerId: 'c-1042',
    kind: 'uptime',
    state: 'failing',
    detail: 'HTTP 500 · 3 consecutive failures',
    region: 'ap-southeast',
    lastRunAt: minutesFromNow(-1),
    durationMs: 1204,
  },
  {
    id: 'f-2',
    domain: 'legacy.northwind.co',
    customer: 'Northwind Retail',
    customerId: 'c-1039',
    kind: 'ssl',
    state: 'failing',
    detail: 'TLS handshake failed · unreadable certificate',
    region: 'eu-central',
    lastRunAt: minutesFromNow(-42),
    durationMs: 5000,
  },
  {
    id: 'f-3',
    domain: 'shop.acme.com',
    customer: 'Acme Internet',
    customerId: 'c-1042',
    kind: 'ssl',
    state: 'warning',
    detail: 'Expires in 9 days · warning sent',
    region: 'ap-southeast',
    lastRunAt: minutesFromNow(-186),
    durationMs: 412,
  },
  {
    id: 'f-4',
    domain: 'checkout.peakmetrics.dev',
    customer: 'Peak Metrics',
    customerId: 'c-1011',
    kind: 'uptime',
    state: 'warning',
    detail: 'p95 1,890ms · above timeout threshold',
    region: 'us-east',
    lastRunAt: minutesFromNow(-3),
    durationMs: 1890,
  },
  {
    id: 'f-5',
    domain: 'verity.health',
    customer: 'Verity Health',
    customerId: 'c-0998',
    kind: 'uptime',
    state: 'ok',
    detail: 'HTTP 200 · 214ms',
    region: 'us-east',
    lastRunAt: minutesFromNow(-2),
    durationMs: 214,
  },
  {
    id: 'f-6',
    domain: 'kestrel.studio',
    customer: 'Kestrel Studio',
    customerId: 'c-1036',
    kind: 'ssl',
    state: 'ok',
    detail: 'Valid 74 days · Let’s Encrypt R11',
    region: 'eu-central',
    lastRunAt: minutesFromNow(-198),
    durationMs: 388,
  },
  {
    id: 'f-7',
    domain: 'solarisfitness.com',
    customer: 'Solaris Fitness',
    customerId: 'c-1004',
    kind: 'ssl',
    state: 'paused',
    detail: 'Paused · subscription cancelled',
    region: 'us-east',
    lastRunAt: daysFromNow(-34),
    durationMs: 0,
  },
  {
    id: 'f-8',
    domain: 'deltafreight.de',
    customer: 'Delta Freight',
    customerId: 'c-1031',
    kind: 'uptime',
    state: 'ok',
    detail: 'HTTP 200 · 341ms',
    region: 'eu-central',
    lastRunAt: minutesFromNow(-4),
    durationMs: 341,
  },
]

export const revenueMonths: RevenueMonth[] = [
  { month: 'Mar', mrr: 402, newMrr: 48, churnedMrr: 12 },
  { month: 'Apr', mrr: 441, newMrr: 54, churnedMrr: 15 },
  { month: 'May', mrr: 468, newMrr: 42, churnedMrr: 15 },
  { month: 'Jun', mrr: 512, newMrr: 61, churnedMrr: 17 },
  { month: 'Jul', mrr: 548, newMrr: 51, churnedMrr: 15 },
  { month: 'Aug', mrr: 583, newMrr: 57, churnedMrr: 22 },
  { month: 'Sep', mrr: 617, newMrr: 49, churnedMrr: 15 },
]

export const staffMembers: StaffMember[] = [
  {
    id: 's-1',
    name: 'Dev Ramachandran',
    email: 'dev@watchtowerworks.com',
    role: 'Superadmin',
    lastActiveAt: minutesFromNow(-2),
    mfa: true,
  },
  {
    id: 's-2',
    name: 'Hana Sørensen',
    email: 'hana@watchtowerworks.com',
    role: 'Support',
    lastActiveAt: minutesFromNow(-47),
    mfa: true,
  },
  {
    id: 's-3',
    name: 'Marcus Bell',
    email: 'marcus@watchtowerworks.com',
    role: 'Billing',
    lastActiveAt: daysFromNow(-1),
    mfa: false,
  },
  {
    id: 's-4',
    name: 'Audit exporter',
    email: 'audit-bot@watchtowerworks.com',
    role: 'Read only',
    lastActiveAt: minutesFromNow(-15),
    mfa: true,
  },
]

export const auditLog: AuditEntry[] = [
  {
    id: 'a-1',
    actor: 'Dev Ramachandran',
    action: 'Granted custom allowance of 25 services',
    target: 'Peak Metrics (c-1011)',
    at: minutesFromNow(-34),
    sensitive: true,
  },
  {
    id: 'a-2',
    actor: 'Hana Sørensen',
    action: 'Resent failed alert email',
    target: 'Northwind Retail (c-1039)',
    at: minutesFromNow(-96),
    sensitive: false,
  },
  {
    id: 'a-3',
    actor: 'Marcus Bell',
    action: 'Issued refund of $15.00',
    target: 'Invoice WT-2036',
    at: minutesFromNow(-320),
    sensitive: true,
  },
  {
    id: 'a-4',
    actor: 'Dev Ramachandran',
    action: 'Signed in as customer (read only)',
    target: 'Acme Internet (c-1042)',
    at: daysFromNow(-1, 11),
    sensitive: true,
  },
  {
    id: 'a-5',
    actor: 'Hana Sørensen',
    action: 'Verified alert recipient manually',
    target: 'ops@deltafreight.de',
    at: daysFromNow(-2, 16),
    sensitive: false,
  },
]
